import { useState } from "react";
import { Calendar, CheckSquare2, Mail, Paperclip, Phone, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MentionTextarea } from "@/components/ui/mention-textarea";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";

type Action = "note" | "call" | "meeting" | "email" | "whatsapp" | "file" | "task";

/** Log something on the deal: write a note right here, or open the call, meeting, email, WhatsApp and file forms */
export function DealComposer({
  text,
  onText,
  busy,
  onSaveNote,
  onCall,
  onMeeting,
  onEmail,
  onWhatsApp,
  onFile,
  onTask,
}: {
  text: string;
  onText: (v: string) => void;
  busy: boolean;
  onSaveNote: () => void;
  onCall: () => void;
  onMeeting: () => void;
  onEmail: () => void;
  onWhatsApp: () => void;
  onFile: () => void;
  onTask?: () => void;
}) {
  const [active] = useState<Action>("note");
  const tabs: { key: Action; label: string; icon: React.ElementType; run?: () => void }[] = [
    { key: "note", label: "Note", icon: StickyNote },
    { key: "call", label: "Call", icon: Phone, run: onCall },
    { key: "meeting", label: "Meeting", icon: Calendar, run: onMeeting },
    { key: "email", label: "Email", icon: Mail, run: onEmail },
    { key: "whatsapp", label: "WhatsApp", icon: WhatsAppIcon, run: onWhatsApp },
    { key: "file", label: "File", icon: Paperclip, run: onFile },
    ...(onTask ? [{ key: "task" as Action, label: "Task", icon: CheckSquare2, run: onTask }] : []),
  ];
  return (
    <div className="flex flex-col rounded-lg border border-border bg-card">
      <div role="group" aria-label="Log activity" className="flex flex-wrap gap-1 border-b border-border/70 px-2.5 py-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            aria-pressed={t.key === active}
            onClick={t.run}
            className={`flex h-[26px] items-center gap-1.5 rounded-md px-2.5 text-xs transition-colors ${t.key === active ? "bg-sidebar font-semibold text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
          >
            <t.icon className="size-3.5" />
            {t.label}
          </button>
        ))}
      </div>
      <label className="px-3.5 pt-2.5">
        <span className="sr-only">Note</span>
        <MentionTextarea
          rows={2}
          value={text}
          onChange={onText}
          placeholder="Write a note about this deal… type @ to mention someone"
          className="w-full resize-none bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
        />
      </label>
      <div className="flex justify-end px-2.5 pb-2.5 pt-1.5">
        <Button size="sm" disabled={busy || !text.trim()} onClick={onSaveNote}>
          Save note
        </Button>
      </div>
    </div>
  );
}
